/**
 * Dossier & casting pipeline (cut 2 — two-wave): compute appearance dossiers for screenplay characters
 * that lack one (heuristic first, free LLM gap-fill when signed in), resolve the cast pick
 * against the live bundled roster, then persist BOTH via the setCharacterDossiers verb —
 * Unity writes the v50 columns; the web keeps its in-memory copy in sync.
 *
 * TWO WAVES (20260709): the LLM gap-fill round-trip measures ~4s and used to gate EVERY pick — the
 * board sat on pre-pick cast bodies for the whole wait ([Scene1Thumb] trace: 87% of the user-visible
 * wrong-cast window). Characters resolvable locally (existing appearance or heuristic intro
 * description) now send IMMEDIATELY (wave 1); only the heuristic gaps wait for the LLM (wave 2).
 * Unity's handler applies each message independently and per-character idempotently, so two messages
 * are safe. Wave 1 must NEVER include a fallback pick for a character the LLM could still describe —
 * a persisted cast_bundled_id makes the missing-filter treat that character as done, locking out the
 * wave-2 refinement — hence wave 1 is defined as the exact complement of the LLM gap set.
 *
 * LLM GAP-FILL KILL SWITCH (20260917, `LLM_GAP_FILL_ENABLED`): with the switch off — the shipped
 * default — wave 2 goes out right behind wave 1 with the gender-pool picks it would have fallen back
 * to anyway, and a pass has no await left in it. See the switch for why.
 *
 * Idempotent per project load (module-level guards) and per character (skips filled rows).
 *
 * THREE LATCHES (20260902). The board now invokes this on every project identity change — first open,
 * reimport refresh, thumbnail repush — because a single per-mount invocation lost the reimport case
 * entirely: the board remounted against the STALE pre-merge project, the screenplay-presence bail
 * fired, `ranForProject` latched onto that object, and the 193-scene merged project that swapped in
 * milliseconds later was never looked at (51 cast rows stranded as random presets). The trigger is now
 * dumb and the decision lives here:
 *   1. IDENTITY — `project === ranForProject`: the same object cannot have changed. Cheap exit.
 *   2. MISSING-SET KEY — the same project CONTENT (title, scene shape, the exact set of rows awaiting
 *      a pick) must not be resolved twice. A repush mints a new project object, so the identity latch
 *      cannot see it; without this a repush landing mid-run or just after the picks persisted would
 *      re-run the LLM gap-fill and re-send. A reimport changes the key and therefore DOES re-run. The
 *      key is CLEARED by the loader on a project SWITCH (a non-refresh setSqlitePath), because it
 *      carries no project id: two files imported from the same script share every term of it.
 *   3. IN-FLIGHT — a call during a run joins the running promise instead of starting a second one; the
 *      runner then re-checks the store once more (bounded loop) so a project that landed mid-run is
 *      picked up now rather than waiting for the next repush.
 */
import { useProjectStore } from '@/store/projectStore.ts';
import { useBlockingStore } from '@/store/blockingStore.ts';
import { buildDossiers, buildWindow, type ImportedScene } from '@/breakdown/dossiers.ts';
import { buildCastIndex } from '@/staging/casting.ts';
import { inferCharacterDossiers } from '@/api/aiApi.ts';
import { sendSetCharacterDossiers } from '@/bridge/features/blocking.ts';
import { diagLog } from '@/bridge/transport.ts';
import type { TimelineProject, TimelineScene } from '@/types/timeline.ts';

let ranForProject: unknown = null;
let lastMissingKey: string | null = null;
let inFlight: Promise<number> | null = null;
// [CastTrace] the key-latch diagnostic fires once per key (see the latch), not once per repush.
let latchHitLogged = false;
// 20260917 - the two cast_placeholder gate counter lines fire once per (missing-set key, counts), not
// once per pass: useCastPipelineTrigger (called from SceneBoardPage on every route) invokes this
// pipeline on every project identity, and the board
// fill's drain mints a new project object per completed scene, so on a 112-scene fill the same
// "29 incomplete row(s) flagged 0" fact was printed 94 times. A changed count or a changed missing
// set still prints — that is the information; the repeats were not.
let lastGateLogKey: string | null = null;

/**
 * Drop the identity and key latches. Called by the loader on a project SWITCH (bridge/features/
 * timeline.ts, the same non-refresh branch that clears the other per-project module caches): the key
 * has no project id in it, so a different project whose title, scene shape and missing id set happen
 * to match the last one — two projects imported from the same .fdx match on every term, since scene
 * and character ids are per-file autoincrements — would otherwise be silently skipped, and its cast
 * stranded exactly as in the reimport incident. A `refresh` re-push is the same project and keeps
 * the latches, which is the whole point of the key.
 *
 * The in-flight promise is deliberately NOT cleared here: a run that is out for the OLD project must
 * still finish (and settle its callers); the runner's store re-check then sees the new project.
 */
export function resetDossierPipelineLatches(): void {
  ranForProject = null;
  lastMissingKey = null;
  latchHitLogged = false;
  lastGateLogKey = null;
}

/** Test-only: all three latches, so suites are order-independent (the key latch is a STRING — two
 *  tests whose fixtures share a shape would otherwise collide across the module instance). */
export function __resetDossierPipelineForTests(): void {
  resetDossierPipelineLatches();
  inFlight = null;
  llmGapFillEnabled = LLM_GAP_FILL_ENABLED;
}

/**
 * 20260917 - LLM GAP-FILL KILL SWITCH (owner, PDF-import retest). The free `infer-characters` call in
 * wave 2 is the ONLY await in a pass, and its only effect on the BODY a character gets is an ethnicity
 * word for characters the script never describes: gender is fixed at import (name lookup, random when
 * the name is unknown) and the pool pick is local either way. On the 2026-09-17 Spider-Verse import
 * that call hung for ~140 s and came back empty, which blew the board fill's 60 s cast-phase cap (the
 * posters rendered with 29 preset stand-ins beside 60 Avaturn bodies), and its late answer on a
 * store-recheck pass then re-picked 20 bodies a second time. With the switch OFF the pass is
 * synchronous: wave 2 follows wave 1 immediately with the same gender-pool picks it would have fallen
 * back to, the recheck loop can never see a repush land mid-run, and the whole cast lands in ONE pass.
 * Flip to true to restore the LLM refinement (the stored appearance phrase for undescribed characters
 * comes back with it).
 */
export const LLM_GAP_FILL_ENABLED = false;
let llmGapFillEnabled = LLM_GAP_FILL_ENABLED;

/** Test-only: the pipeline suite pins BOTH paths. Reset to the shipped default by
 *  `__resetDossierPipelineForTests`. Production never calls this. */
export function __setLlmGapFillEnabledForTests(enabled: boolean): void {
  llmGapFillEnabled = enabled;
}

/**
 * Stable identity for the screenplay-presence bail's diagnostic line. Exported so tests pin "the bail
 * fired" / "the bail did not fire" against this builder instead of a hand-copied substring: a reword
 * then moves both sides at once, and a negative assertion cannot quietly start passing vacuously.
 */
export const LEGACY_PROJECT_GUARD_TAG = '(legacy-project guard)';
export function legacyProjectGuardMessage(sceneCount: number): string {
  return `no screenplay content in ${sceneCount} scenes — cast pipeline skipped ${LEGACY_PROJECT_GUARD_TAG}`;
}

function scenesToImported(scenes: TimelineScene[]): ImportedScene[] {
  return scenes.map((s) => ({
    slugline: s.name || '',
    number: s.number || '',
    actions: (s.screenplay?.actions ?? []).map((a) => a.actionText),
    dialogue: (s.screenplay?.dialogue ?? []).map((d) => ({
      character: d.characterName,
      text: d.dialogueText,
      parenthetical: d.parenthetical,
    })),
  }));
}

/**
 * The project-content discriminator the missing-set latch compares. The store carries no project
 * path or id, so identity is composed: title + scene shape (count and the two edge ids) + the exact
 * set of rows awaiting a pick. A reimport moves the scene shape AND the missing ids; Unity persisting
 * the picks shrinks the missing set. Cross-project collisions are handled by the loader clearing the
 * latches on a project switch (resetDossierPipelineLatches), not by the key.
 *
 * The cast catalog's size is deliberately NOT a term (reviewer, 2026-09-02): the catalog can land
 * DURING a run, and a repush arriving in the same window would then re-key the identical missing set
 * and re-run the LLM gap-fill inside one call — the exact duplicate this latch exists to prevent. The
 * "catalog was empty, only appearances were sent" case re-resolves on the next project switch.
 */
function missingSetKey(project: TimelineProject, missingIds: number[]): string {
  const scenes = project.scenes;
  const firstId = scenes.length > 0 ? scenes[0].id : 0;
  const lastId = scenes.length > 0 ? scenes[scenes.length - 1].id : 0;
  // Numeric sort rather than the default lexicographic one: the ids arrive in roster order, and the
  // key must be a function of the SET, not of the order Unity happened to push the rows in.
  const ids = [...missingIds].sort((a, b) => a - b).join(',');
  return `${project.title}|${scenes.length}|${firstId}-${lastId}|${ids}`;
}

/**
 * A run may re-check the store this many times. The loop exists so a project that swapped in DURING a
 * run (the reimport case: refresh sent while the LLM round-trip is out) is served immediately instead
 * of waiting for the next repush; the cap exists because the loop must terminate under any repush
 * storm. In practice pass 2 is the cheap latch exit — only a genuinely different missing set works.
 */
const MAX_STORE_RECHECK_PASSES = 4;

/**
 * Public entry point — the IN-FLIGHT latch. A call arriving while a run is out joins that run's
 * promise instead of starting a second one: two concurrent passes over the same roster would
 * double-spend the LLM gap-fill and send the same picks twice (Unity's handler is idempotent, but
 * the credits are not).
 */
export function ensureDossiersAndCasting(): Promise<number> {
  if (inFlight) return inFlight;
  inFlight = runWithStoreRecheck().finally(() => { inFlight = null; });
  return inFlight;
}

// 20260909 - CAST PHASE (owner batch 11, item 5, review fix). The `castPipelineDone` message that
// releases Unity's board fill is deliberately NOT sent from here. This function is invoked once per
// project IDENTITY (useCastPipelineTrigger, called from SceneBoardPage on every route), and the fill's own drain mints a new project object
// per completed scene — a 193-scene fill would have answered ~193 times, i.e. a bridge storm plus one
// spurious latch flip per message, during the very fill the phase exists to accelerate. The send lives
// with the caller that ASKED (bridge/features/blocking.ts `requestCastPipeline`): joining an in-flight
// run still settles that handler's `.finally` exactly once, after this run's setCharacterDossiers
// enqueues, so every ordering property the phase depends on holds without the repeats.

async function runWithStoreRecheck(): Promise<number> {
  let total = 0;
  for (let pass = 0; pass < MAX_STORE_RECHECK_PASSES; pass++) {
    const ranOn = useProjectStore.getState().project;
    total += await runPipelinePass();
    // Nothing landed while we were out — the store is what we just ran against, so stop.
    if (useProjectStore.getState().project === ranOn) break;
  }
  return total;
}

async function runPipelinePass(): Promise<number> {
  const project = useProjectStore.getState().project;
  if (!project || project === ranForProject) return 0;

  // SCREENPLAY-PRESENCE BAIL (20260803). INVARIANT: this pipeline may only ever run on a project
  // that actually contains screenplay content — at least one parsed action or dialogue line
  // somewhere in the project. WHY: the v49→v50 migration adds `characters.appearance_desc` and
  // `characters.cast_bundled_id` as EMPTY columns, so on a migrated legacy project (hand-authored
  // cast, no screenplay) those empties are indistinguishable from "never cast" — the missing-filter
  // below reads the whole roster as unfilled and silently recasts every character to a bundled
  // Avaturn body, with the LLM hallucinating appearances from bare character NAMES because there is
  // no screenplay text to feed it. Screenplay presence is the discriminator between the two flows:
  // dossiers + casting exist to serve screenplay-imported projects only.
  // ACCEPTED EDGE: a project caught mid-import (scene rows created, elements not yet written) reads
  // as no-content and is skipped for that one load, picking up on the next project load. That is the
  // safe direction — a skipped pass costs a reload, a wrong pass overwrites a hand-authored cast.
  // 20260902: "the next project load" now costs milliseconds, not a session — useCastPipelineTrigger fires
  // per project identity, so the reimport refresh that swaps the merged project in re-enters here.
  // Latching `ranForProject` on the bail keeps the diagnostic line to once per loaded object.
  const hasScreenplayContent = project.scenes.some(
    (s) => (s.screenplay?.actions?.length ?? 0) > 0 || (s.screenplay?.dialogue?.length ?? 0) > 0,
  );
  if (!hasScreenplayContent) {
    ranForProject = project;
    diagLog('DossierPipeline', legacyProjectGuardMessage(project.scenes.length));
    return 0;
  }

  const roster = project.screenplayCharacters ?? [];
  // v53 cast_placeholder is now REQUIRED, not merely consulted. Emptiness alone could never tell a
  // designed legacy character (deliberately no dossier, real body assigned elsewhere) from a row
  // genuinely awaiting a pick — that ambiguity is what recast the Example project's cast. The flag
  // is provenance: only the import's deferred-pick branch sets it.
  //
  // `=== true` is load-bearing. On a pre-v53 file the loader leaves it undefined, and undefined must
  // NOT pass: for legacy files this pipeline now sends nothing for existing rows. That is the
  // protective direction — a skipped pass costs the user a manual pick, a wrong pass overwrites a
  // hand-authored cast. The cs:5516 screenplay-presence guard above stays as defence in depth.
  const missing = roster.filter((c) => c.name && c.castPlaceholder === true && (!c.appearance || !c.castBundledId));

  // Rows that WOULD have qualified under the old emptiness-only test but are held back by the flag.
  // The two held-back states are counted and reported SEPARATELY, never as one number: flag 0 is Unity
  // telling us this row is not awaiting a pick, while an absent flag is a pre-v53 file that cannot
  // answer the question at all. Merging them into one line would re-assert exactly the ambiguity the
  // column exists to remove — and these logs are the device-log evidence for reasoning about the gate.
  // Counted, not listed: this fires per project load and a per-row line would bury the log.
  let heldFlagZero = 0;     // castPlaceholder === false
  let heldFlagAbsent = 0;   // castPlaceholder undefined (loader's lower tiers) — every other non-true
  for (const c of roster) {
    if (!c.name || c.castPlaceholder === true) continue;
    if (c.appearance && c.castBundledId) continue;
    if (c.castPlaceholder === false) heldFlagZero++; else heldFlagAbsent++;
  }
  // MISSING-SET LATCH (20260902). Computed here — after the presence guard and after the filter, so
  // it describes the work this pass would actually do. Latched BEFORE the awaits below, never after:
  // the repush this defends against (a thumbnail refresh landing mid-run) mints a new project object,
  // so the identity latch above cannot catch it and only an already-written key can.
  const key = missingSetKey(project, missing.map((c) => c.id));

  // The two gate counters report on the roster as pushed, so a repush the key latch turns away can
  // still say what it saw — but only when what it saw CHANGED (20260917, see lastGateLogKey): the
  // same counts against the same missing set is the same sentence, and the fill's per-scene store
  // writes used to print it once per scene.
  const gateLogKey = `${key}#${heldFlagZero}/${heldFlagAbsent}`;
  if (gateLogKey !== lastGateLogKey) {
    lastGateLogKey = gateLogKey;
    if (heldFlagZero > 0) {
      diagLog('DossierPipeline', `cast_placeholder gate: ${heldFlagZero} incomplete row(s) flagged 0 — not awaiting a pick`);
    }
    if (heldFlagAbsent > 0) {
      diagLog('DossierPipeline', `cast_placeholder gate: ${heldFlagAbsent} incomplete row(s) carry no cast_placeholder — pre-v53 file, pending state unknowable`);
    }
  }

  if (key === lastMissingKey) {
    ranForProject = project;
    // Once per key, not once per pass: the board fill's drain writes the store once per scene (a
    // 193-scene project is 193 new project objects, each a pass that lands here), and a line per
    // pass would be a bridge message storm saying the same thing.
    if (!latchHitLogged) {
      latchHitLogged = true;
      diagLog('DossierPipeline', `missing-set key unchanged — the same ${missing.length} row(s) awaiting a pick as the last pass, nothing re-sent (${plain(key)})`);
    }
    return 0;
  }
  lastMissingKey = key;
  latchHitLogged = false;

  if (missing.length === 0) {
    ranForProject = project;
    diagLog('DossierPipeline', `roster ${roster.length}: no row awaiting a pick (cast_placeholder=1 with an empty appearance or cast id) — nothing to do`);
    return 0;
  }
  ranForProject = project;
  // [CastTrace] the pass's shape, first names included: the device log can then be read against Unity's
  // "[CastTrace] setCharacterDossiers: N received" line to see whether what was sent is what arrived.
  diagLog('DossierPipeline', `pass: ${project.scenes.length} scenes, roster ${roster.length}, awaiting a pick ${missing.length} [${plain(missing.slice(0, 8).map((c) => c.name).join(', '))}${missing.length > 8 ? ', …' : ''}]`);

  const imported = scenesToImported(project.scenes);
  const names = roster.map((c) => c.name);
  const dossiers = buildDossiers(imported, names);

  // Resolve casting + collect bridge updates for a subset of `missing`, mutating the roster entries
  // in place (store consumers stay consistent). The catalog is re-read per wave — it is pushed by
  // Unity and may land between waves; a wave that runs before it arrives sends appearance-only rows,
  // which keep failing the missing-filter's cast check and re-resolve on the next project load.
  const resolveUpdates = (subset: typeof missing) => {
    const pool = useBlockingStore.getState().castCatalog;
    const cast = pool.length > 0 ? buildCastIndex(pool) : null;
    const updates: { characterId: number; appearanceDesc: string; castBundledId: string }[] = [];
    for (const c of subset) {
      const appearance = c.appearance || dossiers.get(c.name.toUpperCase()) || '';
      let castBundledId = c.castBundledId || '';
      if (!castBundledId && cast) {
        const member = cast.resolveCastForCharacter(
          { gendertype: c.gender ?? '', charactertype: '', characterid: c.id, customcharacterid: 0, name: c.name },
          { description: appearance || undefined },
        );
        castBundledId = member?.bundledId ?? '';
      }
      if (!appearance && !castBundledId) continue;
      c.appearance = appearance || c.appearance;
      c.castBundledId = castBundledId || c.castBundledId;
      updates.push({ characterId: c.id, appearanceDesc: appearance, castBundledId });
    }
    return updates;
  };

  // The LLM gap set FIRST (identical filter to cut 1), so wave 1 is exactly its complement.
  const gaps = missing.filter((c) => !c.appearance && !dossiers.get(c.name.toUpperCase()));
  const gapIds = new Set(gaps.map((c) => c.id));
  const local = missing.filter((c) => !gapIds.has(c.id));

  // WAVE 1: locally-described characters — send before the multi-second LLM round-trip.
  const wave1 = resolveUpdates(local);
  if (wave1.length > 0) sendSetCharacterDossiers(wave1);
  diagLog('DossierPipeline', `wave 1: ${local.length} locally described, sent ${wave1.length} (catalog ${useBlockingStore.getState().castCatalog.length} bodies); LLM gap set ${gaps.length}`);

  // WAVE 2: free LLM gap-fill (silent-degrades offline), then the remainder — including the
  // stable-hash gender-pool fallback picks for characters the LLM couldn't (or, beyond the 24-char
  // cap, didn't get to) describe.
  if (gaps.length > 0 && !llmGapFillEnabled) {
    // Kill switch off (see LLM_GAP_FILL_ENABLED): no round trip, wave 2 is the gender-pool picks, sent now.
    diagLog('DossierPipeline', `LLM gap-fill BYPASSED (kill switch off) — wave 2 sends gender-pool picks for all ${gaps.length} immediately`);
  } else if (gaps.length > 0) {
    const asked = gaps.slice(0, 24);
    try {
      const inferred = await inferCharacterDossiers(
        asked.map((c) => ({ name: c.name, context: buildWindow(imported, c.name) })),
      );
      let described = 0;
      for (const r of inferred) {
        if (r.appearance) { dossiers.set(r.name.toUpperCase(), r.appearance); described++; }
      }
      diagLog('DossierPipeline', `LLM gap-fill: asked ${asked.length} of ${gaps.length}, described ${described}`);
    } catch (err) {
      // The transport degrades to [] on its own; this catch exists so an unexpected throw is a LOGGED
      // line and wave 2 still sends its gender-pool picks, rather than a silently dead pass.
      diagLog('DossierPipeline', `LLM gap-fill FAILED (${plain(String(err))}) — wave 2 sends gender-pool picks only`);
    }
  }
  const wave2 = resolveUpdates(gaps);
  if (wave2.length > 0) sendSetCharacterDossiers(wave2);
  diagLog('DossierPipeline', `wave 2: sent ${wave2.length} of ${gaps.length} (catalog ${useBlockingStore.getState().castCatalog.length} bodies); ${gaps.length - wave2.length} unresolved (no appearance and no catalog pick); pass total ${wave1.length + wave2.length}`);

  return wave1.length + wave2.length;
}

/** Diagnostic text scrubbed of double quotes: Unity's diagLog sink reads the message up to the first `"`. */
function plain(s: string): string {
  return s.replace(/"/g, "'");
}
